@if (isDataLoaded) {
  <div class="container help-container">

    <!-- Heading -->
    <div class="row">
      <div class="col" align="left">
        <h1 i18n class="blue-help-heading">Help</h1>
      </div>
      <div class="col" align="right">
        @if (showMenu) {
          <app-menu></app-menu>
        }
      </div>
    </div>

    <!-- ── Step-by-Step Guide ── -->
    <h3 i18n class="blue-help-subheading">Step-by-Step Guide</h3>
    <div class="section-divider"></div>

    <mat-accordion>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">person_add</mat-icon>
            <span i18n>Step 1 — Create Your Account</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>Open the app and tap <strong>Create an Account</strong> on the login screen.</li>
          <li i18n>Enter your personal details: first name, last name, Fisher Identification Number (FIN), email address, mailing address, and phone number.</li>
          <li i18n>Read and accept the Terms &amp; Conditions and the privacy notice.</li>
          <li i18n>Your account is created and you are logged in automatically. You will land on the <strong>Home</strong> screen.</li>
        </ol>
        <p i18n class="step-note">Already have an account? Enter your email and password on the login screen to sign in. The app supports offline login using your credentials.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">manage_accounts</mat-icon>
            <span i18n>Step 2 — Set Up Your Profile</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>From the Home screen, open the hamburger menu (<mat-icon class="inline-icon">menu</mat-icon>) and tap <strong>Profile</strong>.</li>
          <li i18n>Under <strong>Vessels</strong>, tap <strong>Add Vessel</strong> and enter your Vessel Registration Number (VRN) and vessel name.</li>
          <li i18n>Your logbooks are automatically loaded during login if you are online. Review them under the <strong>Logbooks</strong> section. A logbook must show as <em>Active</em> before you can start a trip with it.</li>
          <li i18n>If you hold a Gill Net licence, add your nets under the <strong>Nets</strong> section by tapping <strong>Add Net</strong> and filling in the net type, dimensions, and mesh size.</li>
        </ol>
        <p i18n class="step-note">Profile changes are saved locally and synced to the server the next time you are online.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">sailing</mat-icon>
            <span i18n>Step 3 — Start a New Trip</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>From the Home screen, tap <strong>Start New Trip</strong>.</li>
          <li i18n>Select the <strong>Vessel Master</strong> for this trip from the dropdown (this is the FIN of record). If yours is not listed, choose <strong>Add new vessel master</strong> and enter it.</li>
          <li i18n>Read the <strong>Vessel Master Agreement</strong> and check <strong>I AGREE</strong>. This is required to continue.</li>
          <li i18n>Select your <strong>Logbook</strong> from your available logbooks. Only active logbooks are shown; the associated vessel is filled in automatically based on the logbook.</li>
          <li i18n>Choose your <strong>Licence Area</strong>, then select the <strong>Management Area</strong> you intend to fish.</li>
          <li i18n>Set the <strong>Start Date</strong> and <strong>Start Time</strong> (24-hour). Use the clock icon to fill in the current time.</li>
          <li i18n>Optionally add a <strong>Comment</strong> on the final page.</li>
          <li i18n>Review the summary and tap <strong>Submit</strong>. You will receive a FOS confirmation number within 15 minutes. Refresh the Home screen or use the <strong>Update confirmation numbers</strong> button on the Records page to sync confirmation numbers.</li>
        </ol>
        <p i18n class="step-note">After submitting, you are taken to the <strong>Active Trip</strong> screen where you manage all reports for this trip.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">set_meal</mat-icon>
            <span i18n>Step 4 — Submit Daily Catch Reports</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>From the Active Trip screen, tap <strong>Add Catch Report</strong>. The form shown (Gill Net, Seine, or Troll) is determined by your logbook type.</li>
          <li i18n>Select the <strong>Catch Date</strong> for this report.</li>
          <li i18n>Select the <strong>Management Area</strong> fished (must match the Start Fishing Report) and any <strong>Sub-Areas</strong>.</li>
          <li i18n>Enter <strong>Hours Fished</strong> directly, rounded to the nearest half hour (between 0.5 and 24).</li>
          <li>
            <span i18n>Gear-specific fields:</span>
            <ul>
              <li i18n><strong>Gill Net:</strong> on the first page, choose the net you used (or tap <strong>Add New Net</strong>), then enter the <strong>Number of Sets</strong>.</li>
              <li i18n><strong>Seine:</strong> enter the <strong>Number of Sets</strong>.</li>
              <li i18n><strong>Troll:</strong> choose the <strong>Storage Type</strong> (Frozen, Iced, Combined, or Unknown).</li>
            </ul>
          </li>
          <li i18n>In the catch table, tap a species row (or <strong>Add New Species</strong>) to enter the catch for each species.</li>
          <li i18n>Optionally add a <strong>Comment</strong> on the final page.</li>
          <li i18n>Review the summary and tap <strong>Submit</strong>. If offline, the report is saved locally and can be sent to DFO from the <strong>Records</strong> page once you are back online.</li>
        </ol>
        <p i18n class="step-note">You must submit a catch report or pause report for each day of the duration of the trip.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">pause_circle</mat-icon>
            <span i18n>Step 5 — Submit Pause Reports (if applicable)</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>If you did not fish on a given day during the trip (e.g., due to weather or equipment failure), tap <strong>Add Pause Report</strong> from the Active Trip screen.</li>
          <li i18n>Select the <strong>Pause Date</strong> the report covers.</li>
          <li i18n>Select the <strong>Management Area</strong> you intended to fish.</li>
          <li i18n>Optionally add a <strong>Comment</strong> describing the reason for the pause.</li>
          <li i18n>Review the summary and tap <strong>Submit</strong>.</li>
        </ol>
        <p i18n class="step-note">Submit one pause report for each day during the trip that you did not fish. All pause reports must be complete before you can end the trip.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">flag</mat-icon>
            <span i18n>Step 6 — End Your Trip</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>When your trip is finished, tap <strong>End Trip</strong> from the Active Trip screen.</li>
          <li i18n>All daily catch and pause reports must be complete. If any are unfinished, you will be prompted to delete them before proceeding.</li>
          <li i18n>Enter the <strong>End Date</strong> and <strong>End Time</strong> (24-hour) — when you stopped fishing.</li>
          <li i18n>If your licence requires an arrival declaration, enter the <strong>Planned Offload Date and Time</strong> and select the <strong>Designated Landing Site</strong>. If your site is not listed, choose <strong>Not Listed</strong> and describe it in the comments field.</li>
          <li i18n>Optionally add a <strong>Comment</strong> on the final page.</li>
          <li i18n>Review the summary and tap <strong>Submit</strong>. Your trip moves to your trip history.</li>
        </ol>
        <p i18n class="step-note">To cancel a trip entirely instead of ending it, tap <strong>Cancel Trip</strong> from the Active Trip screen. Cancelled trips are saved to your history but marked as cancelled.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">local_shipping</mat-icon>
            <span i18n>Step 7 — Submit Offload Reports (if applicable)</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>After ending your trip, submit an <strong>Offload Report</strong> to document where and how your catch was offloaded.</li>
          <li i18n>Enter the <strong>Offload Date</strong>, <strong>Landing Site</strong>, and <strong>Product Type</strong>.</li>
          <li i18n>Provide the <strong>Last Management Area</strong>, sub-area, and number of days fished.</li>
          <li i18n>If a fish slip was issued, select <strong>Yes</strong> and enter the fish slip number.</li>
          <li i18n>Fill in the species offloaded table with weights or piece counts for each species.</li>
          <li i18n>Tap <strong>Submit</strong>.</li>
        </ol>
        <p i18n class="step-note">You can submit multiple offload reports for the same trip to account for partial offloads at different landing sites.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="step-title">
            <mat-icon class="step-icon">history</mat-icon>
            <span i18n>Step 8 — View Trip History</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <ol class="step-list">
          <li i18n>Open the hamburger menu (<mat-icon class="inline-icon">menu</mat-icon>) and tap <strong>Records</strong>.</li>
          <li i18n>All completed and cancelled trips are listed here in reverse chronological order.</li>
          <li i18n>Tap a trip to view all of its reports along with their FOS confirmation numbers.</li>
        </ol>
        <p i18n class="step-note">Confirmation numbers may take up to 15 minutes to process after submission. The Active Trip screen checks for updates from the DFO server each time it loads.</p>
      </mat-expansion-panel>

    </mat-accordion>

    <!-- ── FAQ ── -->
    <h3 i18n class="blue-help-subheading" style="margin-top: 28px;">Frequently Asked Questions</h3>
    <div class="section-divider"></div>

    <mat-accordion>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>Can I use the app without an internet connection?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">Yes. The app is designed for offline use. All reports are saved locally on your device first. When you regain connection visit your Records page to submit all pending reports.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>What does "Awaiting Response" mean on a report?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">"Awaiting Response" means the report has been submitted to the DFO server but a FOS confirmation number has not been returned yet. This is normal - it can take up to 15 minutes. Refresh the Home screen while online and the app will automatically fetch any outstanding confirmation numbers.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>What is a FOS confirmation number?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">A FOS (Fisheries Online System) confirmation number is the reference number assigned by DFO when a report is successfully received. It is your proof of submission. Confirmation numbers appear on the Home screen next to each submitted report, in the format <em>FOS-XXXXX</em>.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>What do I do if a report shows "Error with submission"?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">"Error with submission" means the report reached DFO but the Fisheries Online System (FOS) returned a failure response instead of a FOS confirmation number. The failure message returned by FOS is shown in the confirmation number field on the report. The report is not on file with DFO until it has been corrected and resubmitted successfully.</p>
        <p i18n class="faq-body">To address it:</p>
        <ol class="faq-body">
          <li i18n>From the <strong>Home</strong> screen, tap the failed report in the reports list to open it.</li>
          <li i18n>Read the message in the confirmation number field. Many FOS messages identify the field at fault (for example, an invalid management area, a date outside an active fishery opening, or a missing value).</li>
          <li i18n>Tap the <strong>Reopen report</strong> button that appears below the confirmation number field. The report becomes editable again.</li>
          <li i18n>Correct the field(s) called out in the FOS message and tap <strong>Submit</strong>. The status returns to <em>Awaiting response</em> until FOS responds again.</li>
        </ol>
        <p i18n class="faq-body">If the FOS message is unclear, or the report continues to fail after you have corrected what it identified, contact support at <strong>SalmonAppHelp&#64;archipelago.ca</strong>. Include your username, the trip start date, and the full text shown in the confirmation number field so support can help diagnose the issue.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>What happens to incomplete reports when I end a trip?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">When you tap End Trip, the app checks for any incomplete daily catch or pause reports. If any exist, a dialog will appear asking whether to delete them before proceeding. Incomplete reports cannot be submitted and must either be completed or deleted before the trip can be ended.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>Can I have multiple active trips at the same time?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">No. Only one trip can be active at a time. You must end or cancel your current trip before you can start a new one.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>How do I cancel a trip?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">From the Active Trip screen, tap <strong>Cancel Trip</strong>. Any incomplete reports will be automatically removed. You will be asked to confirm before the trip is cancelled. Cancelled trips appear in your Records history.</p>
      </mat-expansion-panel>

      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="faq-title">
            <span i18n>How do I reset my password?</span>
          </mat-panel-title>
        </mat-expansion-panel-header>
        <p i18n class="faq-body">On the login screen, tap <strong>Forgot Password?</strong> and follow the instructions to reset your password via email. Password resets require an internet connection.</p>
      </mat-expansion-panel>

    </mat-accordion>

    <!-- ── Contact ── -->
    <h3 i18n class="blue-help-subheading" style="margin-top: 28px;">Contact &amp; Support</h3>
    <div class="section-divider"></div>

    <div class="contact-card">
      <div class="contact-row">
        <mat-icon class="contact-icon">email</mat-icon>
        <div>
          <p i18n class="contact-label">Email Support</p>
          <p class="contact-value">SalmonAppHelp@archipelago.ca</p>
        </div>
      </div>

      <div class="contact-row">
        <mat-icon class="contact-icon">schedule</mat-icon>
        <div>
          <p i18n class="contact-label">Support Hours</p>
          <p i18n class="contact-value">Monday - Friday, 9:00 AM - 4:00 PM Pacific Time</p>
        </div>
      </div>
    </div>

    <!-- ── App Info ── -->
    <h3 i18n class="blue-help-subheading" style="margin-top: 28px;">About This App</h3>
    <div class="section-divider"></div>

    <div class="about-section">
      <div class="about-row">
        <span i18n class="about-label">Version</span>
        <span class="about-value">{{ appVersion }}</span>
      </div>
      <div class="about-row">
        <span i18n class="about-label">Terms &amp; Conditions</span>
        <a [href]="tosPath" target="_blank" rel="noopener noreferrer" class="tos-link">
          <span i18n>View Schedule A — Terms and Conditions</span>
          <mat-icon class="tos-icon">open_in_new</mat-icon>
        </a>
      </div>
    </div>

    <div style="height: 40px;"></div>

  </div>
}
